import React, { useState } from 'react';
import styles from './ProductForm.module.css';

function ProductForm() {
    const initialFormData = {
        Name: '',
        Brand: '',
        Unit: '',
        Country: '',
        Price: '',
        formType: '1',
        ETA: ''
    };

    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const formatPrice = (value) => {
        // Remove non-numeric characters except decimal point
        const numericValue = value.replace(/[^\d.]/g, '');
        
        // Ensure only one decimal point
        const parts = numericValue.split('.');
        if (parts.length > 2) {
            return parts[0] + '.' + parts.slice(1).join('');
        }
        
        // Limit to 2 decimal places
        if (parts.length === 2 && parts[1].length > 2) {
            return parts[0] + '.' + parts[1].substring(0, 2);
        }
        
        return numericValue;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        let processedValue = value;
        if (name === 'Price') {
            processedValue = formatPrice(value);
        }
        
        setFormData(prevFormData => ({
            ...prevFormData,
            [name]: processedValue
        }));
        
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prevErrors => ({
                ...prevErrors,
                [name]: ''
            }));
        }
        
        // Clear message when user starts editing
        if (message.text) {
            setMessage({ type: '', text: '' });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        // Required field validation
        if (!formData.Name?.trim()) {
            newErrors.Name = '名稱為必填項目';
        }
        
        if (!formData.Brand?.trim()) {
            newErrors.Brand = '品牌為必填項目';
        }
        
        if (!formData.Country?.trim()) {
            newErrors.Country = '產地為必填項目';
        }
        
        if (!formData.Price?.toString().trim()) {
            newErrors.Price = '價錢為必填項目';
        } else if (isNaN(formData.Price) || parseFloat(formData.Price) <= 0) {
            newErrors.Price = '請輸入有效的價錢';
        } else if (!/^\d+(\.\d{1,2})?$/.test(formData.Price.toString())) {
            newErrors.Price = '請輸入有效格式的價錢 (例如: 100 或 100.50)';
        }
        
        if (!formData.Unit?.trim()) {
            newErrors.Unit = '單位為必填項目';
        }
        
        // ETA validation only for futures (formType === '2')
        if (formData.formType === '2' && !formData.ETA?.trim()) {
            newErrors.ETA = 'ETA為必填項目';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form before submission
        if (!validateForm()) {
            setMessage({ type: 'error', text: '請填寫所有必填項目' });
            return;
        }

        setIsLoading(true);
        setMessage({ type: '', text: '' });

        const data = [{
            name: formData.Name,
            brand: formData.Brand,
            unit: formData.Unit,
            country: formData.Country,
            price: parseFloat(formData.Price),
            ...(formData.formType === '2' && { eta: formData.ETA })
        }];

        console.log('Submitting form data:', data);

        var apiURL = process.env.REACT_APP_API_URL;

        if (formData.formType === '1') {
            apiURL = apiURL.concat('/v1/insertProduct');
        } else {
            apiURL = apiURL.concat('/v1/insertFuture');
        }

        try {
            const response = await fetch(apiURL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`伺服器錯誤 (${response.status}): ${errorText || '請稍後再試'}`);
            }

            const responseData = await response.json();
            console.log('Success:', responseData);
            
            setMessage({ type: 'success', text: '表單提交成功！' });
            
            // Reset form after successful submission but preserve formType
            setFormData(prevData => ({
                ...initialFormData,
                formType: prevData.formType
            }));
            setErrors({});
            
        } catch (error) {
            console.error('Error:', error);
            let errorMessage = '提交失敗，請稍後再試';
            
            if (error.message.includes('fetch')) {
                errorMessage = '網絡連接失敗，請檢查您的網絡連接';
            } else if (error.message.includes('伺服器錯誤')) {
                errorMessage = error.message;
            }
            
            setMessage({ type: 'error', text: errorMessage });
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        setFormData(initialFormData);
        setErrors({});
        setMessage({ type: '', text: '' });
    };

    return (
        <div className={styles.formContainer}>
            <h1>網上報價</h1>
            
            {message.text && (
                <div className={`${styles.message} ${styles[message.type]}`}>
                    {message.text}
                </div>
            )}
            
            <form onSubmit={handleSubmit}>
                <label className={styles.label}>
                    報價類型:
                    <select className={styles.input} name="formType" value={formData.formType} onChange={handleChange}>
                        <option value="1">現貨</option>
                        <option value="2">期貨</option>
                    </select>
                </label>
                <label className={styles.label}>
                    產地: <span className={styles.required}>*</span>
                    <input 
                        className={`${styles.input} ${errors.Country ? styles.inputError : ''}`} 
                        type="text" 
                        name="Country" 
                        value={formData.Country} 
                        onChange={handleChange}
                        placeholder="請輸入產地"
                    />
                    {errors.Country && <span className={styles.errorText}>{errors.Country}</span>}
                </label>
                <label className={styles.label}>
                    名稱: <span className={styles.required}>*</span>
                    <input 
                        className={`${styles.input} ${errors.Name ? styles.inputError : ''}`} 
                        type="text" 
                        name="Name" 
                        value={formData.Name} 
                        onChange={handleChange}
                        placeholder="請輸入產品名稱"
                    />
                    {errors.Name && <span className={styles.errorText}>{errors.Name}</span>}
                </label>
                <label className={styles.label}>
                    品牌: <span className={styles.required}>*</span>
                    <input 
                        className={`${styles.input} ${errors.Brand ? styles.inputError : ''}`} 
                        type="text" 
                        name="Brand" 
                        value={formData.Brand} 
                        onChange={handleChange}
                        placeholder="請輸入品牌"
                    />
                    {errors.Brand && <span className={styles.errorText}>{errors.Brand}</span>}
                </label>
                <label className={styles.label}>
                    價錢 (港幣): <span className={styles.required}>*</span>
                    <div className={styles.priceInputContainer}>
                        <span className={styles.currencyPrefix}>HK$</span>
                        <input 
                            className={`${styles.input} ${styles.priceInput} ${errors.Price ? styles.inputError : ''}`} 
                            type="text" 
                            name="Price" 
                            value={formData.Price} 
                            onChange={handleChange}
                            placeholder="100 或 100.50"
                        />
                    </div>
                    {errors.Price && <span className={styles.errorText}>{errors.Price}</span>}
                </label>
                <label className={styles.label}>
                    單位: <span className={styles.required}>*</span>
                    <input 
                        className={`${styles.input} ${errors.Unit ? styles.inputError : ''}`} 
                        type="text" 
                        name="Unit" 
                        value={formData.Unit} 
                        onChange={handleChange}
                        placeholder="請輸入單位 (例如: 公斤, 件, 箱)"
                    />
                    {errors.Unit && <span className={styles.errorText}>{errors.Unit}</span>}
                </label>
                {formData.formType === '2' && (
                    <label className={styles.label}>
                        ETA: <span className={styles.required}>*</span>
                        <input 
                            className={`${styles.input} ${errors.ETA ? styles.inputError : ''}`} 
                            type="date" 
                            name="ETA" 
                            value={formData.ETA} 
                            onChange={handleChange}
                        />
                        {errors.ETA && <span className={styles.errorText}>{errors.ETA}</span>}
                    </label>
                )}
                <div className={styles.buttonGroup}>
                    <button 
                        className={styles.button} 
                        type="submit" 
                        disabled={isLoading}
                    >
                        {isLoading ? '提交中...' : '提交'}
                    </button>
                    <button 
                        className={styles.resetButton} 
                        type="button" 
                        onClick={handleReset}
                        disabled={isLoading}
                    >
                        重置
                    </button>
                </div>
            </form>
        </div>
    );
}

export default ProductForm;
